import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { MatDialog } from '@angular/material/dialog';
import { ExpandModalDialog } from 'app/modules/well-integrity-management/component/expand-modal/expand-modal.component';
import { WellIntergrityManagementService } from '../../service/well-intergrity-management.service';
import { CommonModule } from '@angular/common';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'monitoring-index',
    templateUrl: './monitoring-index.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgTemplateOutlet, CommonModule],
    styleUrls: ['./monitoring-index.component.scss'],
})
export class MonitoringIndexComponent implements OnInit {
    @Input() expandIcon: any;

    kpiList: any[] = [];
    allKpiData: any[] = [];
    chart: any;

    constructor(
        private elementRef: ElementRef,
        private dialog: MatDialog,
        private _wimService: WellIntergrityManagementService,
        private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.getKpiBaselines();
    }

    getKpiBaselines() {
        this._wimService.getDashboardKpiAll().subscribe({
            next: (res) => {
                if (res && res.isSuccess && res.data && res.data.length > 0) {
                    const currentYear = new Date().getFullYear();
                    const allowedYears = [currentYear, currentYear - 1];

                    // Deduplicate and filter by year
                    const uniqueData = [];
                    const seen = new Set();
                    for (const item of res.data) {
                        const key = `${item.kpiNumber}-${item.year}`;
                        if (
                            !seen.has(key) &&
                            allowedYears.includes(Number(item.year))
                        ) {
                            seen.add(key);
                            uniqueData.push(item);
                        }
                    }

                    this.allKpiData = uniqueData;
                    // For the list, only current year
                    this.kpiList = uniqueData.filter(
                        (item) => Number(item.year) === currentYear,
                    );

                    this.createRadarChart();
                    this._cdr.markForCheck();
                } else {
                    this.kpiList = [];
                    this.allKpiData = [];
                    this.createRadarChart();
                    this._cdr.markForCheck();
                }
            },
            error: (err) => {
                console.error('Error fetching KPI baselines:', err);
                this.createRadarChart();
            },
        });
    }

    openDialog(title: string, componentName: string, functionName: string) {
        var component = document.getElementById(componentName);

        const dialogRef = this.dialog.open(ExpandModalDialog, {
            data: {
                title: title,
                viewComponent: component,
                functionName: functionName,
                apiData: { data: this.allKpiData },
            }, // Pass your parameters here
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log(`Dialog result: ${result}`);
        });
    }

    formatKpiDisplay(title: string, value: number): string {
        if (!title) return '';
        if (title.includes('{baseline}')) {
            return title.replace('{baseline}', value.toString());
        }
        return `${title} (${value}%)`;
    }

    createRadarChart() {
        if (this.chart) {
            this.chart.dispose();
        }

        /* Create chart instance */
        let chart = am4core.create('radarChart', am4charts.RadarChart) as any;
        chart.logo.disabled = true;

        chart.maskBullets = false;
        chart.padding(30, 30, 30, 30);

        /* Prepare Data */
        const currentYear = new Date().getFullYear();
        const relevantKpis = this.allKpiData;

        const kpiNumbers = Array.from(
            new Set(relevantKpis.map((k) => k.kpiNumber)),
        ).sort((a, b) => a - b);

        const years = Array.from(
            new Set(relevantKpis.map((k) => Number(k.year))),
        ).sort((a, b) => a - b);

        chart.data = kpiNumbers.map((num) => {
            const dataObj: any = {
                shortLabel: `KPI ${num}`,
            };

            years.forEach((year) => {
                const kpi = relevantKpis.find(
                    (k) => k.kpiNumber === num && Number(k.year) === year,
                );
                if (kpi) {
                    const actualValue = (num === 4 || num === 5) ? (kpi.isAchieved ? 100 : 0) : (kpi.actualPercentage || 0);
                    dataObj[year.toString()] = actualValue;
                    dataObj[`${year}_label`] = `KPI ${num} : ${actualValue}%`;
                }
            });

            return dataObj;
        });

        /* Create axes */
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'shortLabel';
        categoryAxis.renderer.labels.template.fill = am4core.color('#000');

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.renderer.axisFills.template.fill = chart.colors.getIndex(2);
        valueAxis.renderer.axisFills.template.fillOpacity = 0.05;
        valueAxis.renderer.gridType = 'polygons';
        valueAxis.renderer.labels.template.disabled = true;

        /* Create and configure series */
        years.forEach((year) => {
            let series = chart.series.push(new am4charts.RadarSeries());
            series.dataFields.valueY = year.toString();
            series.dataFields.categoryX = 'shortLabel';
            series.name = year.toString();
            series.strokeWidth = 2;
            series.fillOpacity = 0.2;
            series.tooltipText = `{${year}_label}`;

            let bullet = series.bullets.push(new am4charts.CircleBullet());
            bullet.circle.radius = 4;
            bullet.circle.strokeWidth = 2;
            bullet.circle.fill = am4core.color('#fff');

            if (year === currentYear) {
                series.fill = am4core.color('#93bfeb');
                series.stroke = am4core.color('#93bfeb');
                bullet.circle.stroke = am4core.color('#93bfeb');
            } else {
                series.fill = am4core.color('#d87c34');
                series.stroke = am4core.color('#d87c34');
                bullet.circle.stroke = am4core.color('#d87c34');
            }
        });

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;

        chart.cursor = new am4charts.RadarCursor();

        removeAmChartLogo(this.elementRef.nativeElement);

        this.chart = chart;
    }
}
